import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { TackCard } from "@/components/TackCard";
import { TackJourneyPreview } from "@/components/TackJourneyPreview";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOCALE_LABELS, LOCALE_GROUPS, getLocaleDefaults } from "@/lib/onboarding-defaults";
import { REDEEM_PAGE_STRINGS } from "@/lib/redeem-page-strings";
import { stepHasBookingCta } from "@/lib/booking-cta-steps";
import {
  buildRewardBody,
  buildNudgeBody,
  appendSwedishFooter,
  normalizeSmsLocale,
} from "@/lib/sms/message-bodies";
import { getSeSmsNumberFn } from "@/lib/sms-config.functions";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/tack-preview/$slug")({
  component: TackPreviewPage,
});

type Step =
  | "form"
  | "submitted"
  | "sms_preview"
  | "email"
  | "ready"
  | "redeem"
  | "pending"
  | "pending_review"
  | "has_active_code"
  | "recent_scan"
  | "cooldown";

// Only the two texts merchants actually tweak; everything else follows the locale.
const EDITABLE_FIELDS = [
  "brand_welcome_text",
  "feedback_prompt",
] as const;

type EditableField = typeof EDITABLE_FIELDS[number];

// All locale-derived strings (used to re-translate when language changes).
const ALL_LOCALE_FIELDS = [
  "brand_welcome_text", "feedback_prompt", "email_label", "cta_text",
  "thank_you_text", "discount_code_label", "discount_subtext",
  "redeem_button_text", "save_code_hint", "activates_in_text",
  "email_when_ready_text", "feedback_optional_text", "show_cashier_text",
  "check_inbox_text",
] as const;

function TackPreviewPage() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const [merchant, setMerchant] = useState<Record<string, any> | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [previewStep, setPreviewStep] = useState<Step>("form");
  const [locale, setLocale] = useState("en");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [fields, setFields] = useState<Record<EditableField, string>>({
    brand_welcome_text: "",
    feedback_prompt: "",
  });
  const [seSmsNumber, setSeSmsNumber] = useState<string | null>(null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchSeNumber = useServerFn(getSeSmsNumberFn);

  useEffect(() => {
    fetchSeNumber()
      .then((r) => setSeSmsNumber(r?.number?.trim() || null))
      .catch(() => setSeSmsNumber(null));
  }, [fetchSeNumber]);

  useEffect(() => {
    supabase
      .from("merchants")
      .select("id, business_name, promo_discount_percent, promo_activation_hours, brand_logo_url, locale, redemption_style, booking_url, booking_url_status, sms_optout_code, reward_expiry_months, " + [...EDITABLE_FIELDS, "thank_you_text", "discount_code_label", "discount_subtext", "redeem_button_text", "save_code_hint", "activates_in_text", "email_when_ready_text", "show_cashier_text", "check_inbox_text"].join(", "))
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setMerchant(data);
          setLocale((data as any).locale || "en");
          const updated: Record<string, string> = {};
          for (const f of EDITABLE_FIELDS) {
            updated[f] = (data as any)[f] || "";
          }
          setFields(updated as Record<EditableField, string>);
        }
        setLoaded(true);
      });
  }, [slug]);

  const saveField = useCallback(
    (field: EditableField, value: string) => {
      if (!merchant) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        supabase
          .from("merchants")
          .update({ [field]: value || null } as any)
          .eq("id", merchant.id)
          .then(() => {});
      }, 800);
    },
    [merchant]
  );

  const updateField = (field: EditableField, value: string, max: number) => {
    if (value.length > max) return;
    setFields((prev) => ({ ...prev, [field]: value }));
    saveField(field, value);
  };

  // Change language: persist locale, re-fill all locale-derived fields with defaults.
  const changeLocale = (newLocale: string) => {
    if (!merchant) return;
    setLocale(newLocale);
    const d = getLocaleDefaults(newLocale);
    const pct = merchant.promo_discount_percent || 10;
    const updates: Record<string, string | null> = {
      locale: newLocale,
      brand_welcome_text: d.welcomeText,
      feedback_prompt: d.feedbackPrompt,
      email_label: d.emailLabel,
      cta_text: d.getDiscountCta.replace("{X}", String(pct)),
      thank_you_text: d.thankYou,
      discount_code_label: d.discountCodeLabel,
      discount_subtext: d.discountSubtext.replace("{X}", String(pct)),
      redeem_button_text: d.redeemButton,
      save_code_hint: d.saveCodeHint,
      activates_in_text: d.activatesIn,
      email_when_ready_text: d.emailWhenReady.replace("{X}", String(pct)),
      feedback_optional_text: d.feedbackOptional,
      show_cashier_text: d.showCashier,
      check_inbox_text: d.checkInbox,
    };
    setFields((prev) => ({
      ...prev,
      brand_welcome_text: d.welcomeText,
      feedback_prompt: d.feedbackPrompt,
      email_label: d.emailLabel,
      cta_text: d.getDiscountCta.replace("{X}", String(pct)),
      feedback_optional_text: d.feedbackOptional,
    }));
    setMerchant((prev) => prev ? { ...prev, ...updates } : prev);
    supabase.from("merchants").update(updates as any).eq("id", merchant.id).then(() => {});
  };

  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <p className="text-muted-foreground">{t("tackPreview.loading")}</p>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-muted/40">
        <p className="text-xl text-muted-foreground">{t("tackPreview.notFound")}</p>
      </div>
    );
  }

  const themeColor = "hsl(20, 55%, 60%)";
  const localeDefaults = getLocaleDefaults(locale);

  const liveMerchant = {
    business_name: merchant.business_name,
    promo_discount_percent: merchant.promo_discount_percent,
    brand_logo_url: merchant.brand_logo_url || null,
    brand_tagline: null,
    brand_welcome_text: fields.brand_welcome_text || null,
    feedback_prompt: fields.feedback_prompt || null,
    locale,
  };

  const coreFlow: { step: Step; label: string; caption: string }[] = [
    { step: "form", label: t("tackPreview.core.form"), caption: t("tackPreview.core.formCaption") },
    { step: "submitted", label: t("tackPreview.core.submitted"), caption: t("tackPreview.core.submittedCaption") },
    { step: "sms_preview", label: t("tackPreview.core.smsPreview"), caption: t("tackPreview.core.smsPreviewCaption") },
    { step: "ready", label: t("tackPreview.core.ready"), caption: t("tackPreview.core.readyCaption") },
    { step: "redeem", label: t("tackPreview.core.redeem"), caption: t("tackPreview.core.redeemCaption") },
  ];

  // Build the exact SMS bodies the customer would receive by calling the
  // same body builders the server uses (`src/lib/sms/message-bodies.ts`).
  const smsLocale = normalizeSmsLocale(locale);
  const hasApprovedBooking =
    merchant.booking_url_status === "approved" &&
    typeof merchant.booking_url === "string" &&
    merchant.booking_url.trim().length > 0;
  const smsRedeemUrl = "tack.company/r/ab12cd";
  const expiryMonths = Math.max(0, Math.min(24, (merchant.reward_expiry_months as number | null) ?? 3));
  // Sample expiry ~2 weeks out so the nudge date is realistic, formatted D/M
  // by the shared builder so it matches production output exactly.
  const sampleNudgeExpiry = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  const optoutCode = (merchant.sms_optout_code as string | null) || "XXXX";
  // For Swedish previews we simulate a +46 recipient so the shared footer
  // helper attaches the compliance line the customer actually sees.
  const previewPhone = smsLocale === "sv" ? "+46700000000" : "+15550000000";

  const rewardSmsBody = appendSwedishFooter(
    buildRewardBody({
      businessName: merchant.business_name,
      discountPercent: merchant.promo_discount_percent,
      redeemUrl: smsRedeemUrl,
      locale: smsLocale,
      expiryMonths,
      hasBookingUrl: hasApprovedBooking,
    }),
    previewPhone,
    optoutCode,
    seSmsNumber,
  );
  const nudgeSmsBody = appendSwedishFooter(
    buildNudgeBody({
      businessName: merchant.business_name,
      discountPercent: merchant.promo_discount_percent,
      redeemUrl: smsRedeemUrl,
      locale: smsLocale,
      expiresAtIso: sampleNudgeExpiry,
      hasBookingUrl: hasApprovedBooking,
    }),
    previewPhone,
    optoutCode,
    seSmsNumber,
  );

  const bookingUrlTrimmed = hasApprovedBooking ? merchant.booking_url.trim() : "";

  const otherStates: { step: Step; label: string; caption: string }[] = [
    { step: "pending_review", label: t("tackPreview.other.pendingReview"), caption: t("tackPreview.other.pendingReviewCaption") },
    { step: "pending", label: t("tackPreview.other.pending"), caption: t("tackPreview.other.pendingCaption") },
    { step: "has_active_code", label: t("tackPreview.other.hasActiveCode"), caption: t("tackPreview.other.hasActiveCodeCaption") },
    { step: "recent_scan", label: t("tackPreview.other.recentScan"), caption: t("tackPreview.other.recentScanCaption") },
    { step: "cooldown", label: t("tackPreview.other.cooldown"), caption: t("tackPreview.other.cooldownCaption") },
  ];

  const renderSmsPreview = () => (
    <div
      style={{
        width: 320,
        height: 580,
        backgroundColor: "#F9F7F2",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ textAlign: "center", fontSize: 13, color: "#666", fontWeight: 600 }}>
        SMS · Tack
      </div>
      {[rewardSmsBody, nudgeSmsBody].map((body, i) => (
        <div
          key={i}
          style={{
            alignSelf: "flex-start",
            maxWidth: "88%",
            background: "#E5E5EA",
            color: "#111",
            padding: "12px 14px",
            borderRadius: 18,
            fontSize: 14,
            lineHeight: 1.35,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {body}
        </div>
      ))}
      <div style={{ textAlign: "center", fontSize: 11, color: "#999", marginTop: "auto" }} />
    </div>
  );

  const renderPreviewCard = (s: Step) => (
    <div
      className="rounded-xl shadow-md overflow-hidden cursor-pointer transition-all relative"
      style={{
        width: 160,
        height: 290,
        border: previewStep === s ? `2px solid ${themeColor}` : "1.5px solid #e5e5e5",
        backgroundColor: "#F9F7F2",
      }}
      onClick={() => setPreviewStep(s)}
    >
      <div style={{ transform: "scale(0.5)", transformOrigin: "top left", width: 320, height: 580 }}>
        {s === "sms_preview" ? renderSmsPreview() : (
          <TackCard
            merchant={liveMerchant}
            step={s as Exclude<Step, "sms_preview">}
            rating={4}
            promoCode="1234"
            activationHours={merchant.promo_activation_hours || 24}
            // Editable form fields override locale defaults; everything else falls back to locale.
            emailLabel={localeDefaults.emailLabel}
            ctaText={localeDefaults.getDiscountCta.replace("{X}", String(merchant.promo_discount_percent))}
            optionalPlaceholder={localeDefaults.feedbackOptional}
            thankYouText={merchant.thank_you_text || localeDefaults.thankYou}
            discountCodeLabel={merchant.discount_code_label || localeDefaults.discountCodeLabel}
            discountSubtext={merchant.discount_subtext || localeDefaults.discountSubtext.replace("{X}", String(merchant.promo_discount_percent))}
            redeemButtonText={merchant.redeem_button_text || localeDefaults.redeemButton}
            saveCodeHint={merchant.save_code_hint || localeDefaults.saveCodeHint}
            activatesInText={merchant.activates_in_text || localeDefaults.activatesIn}
            emailWhenReadyText={merchant.email_when_ready_text || localeDefaults.emailWhenReady.replace("{X}", String(merchant.promo_discount_percent))}
            showCashierText={merchant.show_cashier_text || localeDefaults.showCashier}
            validUntilText={merchant.valid_until_text || localeDefaults.validUntil}
            checkInboxText={merchant.check_inbox_text || localeDefaults.checkInbox}
            submittedSentText={localeDefaults.submittedSent.replace("{X}", String(merchant.promo_discount_percent))}
            submittedClaimText={localeDefaults.submittedClaim}
            submittedSpamText={localeDefaults.submittedSpam}
            startButtonText={localeDefaults.startButton}
            startingButtonText={localeDefaults.startingButton}
            readyExplainerText={localeDefaults.readyExplainer}
            readyValidForDaysTemplate={localeDefaults.readyValidForDays}
            readyValidUntilTomorrowText={localeDefaults.readyValidUntilTomorrow}
            cooldownText={localeDefaults.cooldownBody}
            cooldownHeaderText={localeDefaults.cooldownHeader}
            welcomeBackHeaderText={localeDefaults.welcomeBackHeader}
            justAMomentHeaderText={localeDefaults.justAMomentHeader}
            pendingReviewThanksText={localeDefaults.pendingReviewThanks}
            pendingReviewBodyTemplate={localeDefaults.pendingReviewBody}
            pendingLockedBodyText={
              (smsLocale === "sv" ? REDEEM_PAGE_STRINGS.sv : REDEEM_PAGE_STRINGS.en).notYetActiveBody
            }
            pendingLinkValidText={
              (smsLocale === "sv" ? REDEEM_PAGE_STRINGS.sv : REDEEM_PAGE_STRINGS.en).notYetActiveLinkValid
            }
            recentScanHeaderText={localeDefaults.recentScanHeader}
            recentScanBodyText={localeDefaults.recentScanBody}
            hasActiveCodeHintText={localeDefaults.hasActiveCodeHint}
            resendToEmailButtonText={localeDefaults.resendToEmailButton}
            smsSentHeaderText={localeDefaults.smsSentHeader}
            smsSentBodyText={localeDefaults.smsSentBody}
            sendingShortText={localeDefaults.sendingShort}
            codeLabelInlineText={localeDefaults.codeLabelInline}
            submittingButtonText={localeDefaults.submittingButton}
            emailPlaceholderText={localeDefaults.emailPlaceholder}
            hourLabel={localeDefaults.hour}
            hoursLabel={localeDefaults.hours}
            // Phone + dual consent — match /tack/$slug production wiring.
            phoneLabelText={localeDefaults.phoneLabel}
            phonePlaceholderText={localeDefaults.phonePlaceholder}
            consentTransactionalText={localeDefaults.consentTransactional}
            consentMarketingText={localeDefaults.consentMarketing}
          />
        )}
      </div>
      {stepHasBookingCta(s) && hasApprovedBooking ? (
        <div
          className="absolute left-0 right-0 bottom-0 px-2 pb-2 pt-1"
          style={{ background: "linear-gradient(to top, #F9F7F2 70%, rgba(249,247,242,0))" }}
        >
          <div
            aria-hidden="true"
            className="block w-full text-center rounded-md border border-primary/30 bg-primary/5 text-primary px-2 py-1.5 text-[10px] font-semibold truncate select-none"
          >
            {(smsLocale === "sv" ? REDEEM_PAGE_STRINGS.sv : REDEEM_PAGE_STRINGS.en).bookNext ?? "Book next visit →"}
          </div>
        </div>
      ) : null}
    </div>
  );



  const editableField = (field: EditableField, label: string, max: number) => (
    <div key={field} className="space-y-0.5">
      <Label htmlFor={field} className="text-xs">{label}</Label>
      <Input
        id={field}
        className="h-8 text-xs bg-white"
        value={fields[field]}
        onChange={(e) => updateField(field, e.target.value, max)}
        maxLength={max}
      />
      <p className="text-[10px] text-muted-foreground text-right leading-none">{fields[field].length}/{max}</p>
    </div>
  );

  return (
    <>
      <div className="min-h-screen overflow-auto bg-[#E8E4DF] p-4">
        <div className="max-w-[1100px] mx-auto space-y-4">
          {/* Header: language picker */}
          <div className="rounded-lg p-3 flex items-center gap-3" style={{ border: `1.5px solid ${themeColor}`, backgroundColor: "#FFFFFF" }}>
            <div className="flex-1">
              <h2 className="text-sm font-semibold" style={{ color: themeColor }}>{t("tackPreview.headerTitle")}</h2>
              <p className="text-[11px] text-muted-foreground">{t("tackPreview.headerSubtitle")}</p>
            </div>
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <Label className="text-[10px] text-muted-foreground">{t("tackPreview.language")}</Label>
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  {t("tackPreview.resetToDefaults")}
                </button>
              </div>
              <select
                value={locale}
                onChange={(e) => changeLocale(e.target.value)}
                className="flex h-8 rounded-md border border-input bg-white px-2 text-xs"
              >
                {LOCALE_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.codes.map((code) => (
                      <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          {/* Editable fields — only 6, matching onboarding step 2 */}
          <div className="rounded-lg p-4 space-y-3" style={{ border: `1.5px solid ${themeColor}`, backgroundColor: "#FFFFFF" }}>
            <p className="text-xs text-muted-foreground italic">{t("tackPreview.editIntro")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {editableField("brand_welcome_text", t("tackPreview.field_welcome"), 60)}
              {editableField("feedback_prompt", t("tackPreview.field_prompt"), 80)}
            </div>
            <p className="text-[10px] italic text-muted-foreground">{t("tackPreview.autosaveHint")}</p>
          </div>

          {/* Lane 1 — Core value flow */}
          <div className="rounded-lg p-4" style={{ border: `1.5px solid ${themeColor}`, backgroundColor: "#FFFFFF" }}>
            <h2 className="text-sm font-semibold" style={{ color: themeColor }}>
              {t("tackPreview.coreTitle")}
            </h2>
            <p className="text-[11px] text-muted-foreground italic mb-3">
              {t("tackPreview.coreSubtitle")}
            </p>
            <TackJourneyPreview
              merchant={{
                business_name: merchant.business_name,
                promo_discount_percent: merchant.promo_discount_percent,
                promo_activation_hours: merchant.promo_activation_hours,
                brand_logo_url: merchant.brand_logo_url,
                brand_welcome_text: fields.brand_welcome_text,
                feedback_prompt: fields.feedback_prompt,
                booking_url: merchant.booking_url,
                booking_url_status: merchant.booking_url_status,
                sms_optout_code: merchant.sms_optout_code,
                reward_expiry_months: merchant.reward_expiry_months,
                thank_you_text: merchant.thank_you_text,
                discount_code_label: merchant.discount_code_label,
                discount_subtext: merchant.discount_subtext,
                redeem_button_text: merchant.redeem_button_text,
                save_code_hint: merchant.save_code_hint,
                activates_in_text: merchant.activates_in_text,
                email_when_ready_text: merchant.email_when_ready_text,
                show_cashier_text: merchant.show_cashier_text,
                valid_until_text: merchant.valid_until_text,
                check_inbox_text: merchant.check_inbox_text,
              }}
              locale={locale}
              selectedStep={
                previewStep === "form" || previewStep === "submitted" ||
                previewStep === "sms_preview" || previewStep === "ready" ||
                previewStep === "redeem"
                  ? previewStep
                  : undefined
              }
              onSelectStep={(s) => setPreviewStep(s)}
              themeColor={themeColor}
              redemptionStyle={merchant.redemption_style ?? "quick_burn"}
            />
          </div>

          {/* Lane 2 — Other screens */}
          <div className="rounded-lg p-4" style={{ border: `1.5px solid ${themeColor}`, backgroundColor: "#FFFFFF" }}>
            <h2 className="text-sm font-semibold" style={{ color: themeColor }}>
              {t("tackPreview.otherTitle")}
            </h2>
            <p className="text-[11px] text-muted-foreground italic mb-3">
              {t("tackPreview.otherSubtitle")}
            </p>
            <div className="flex gap-2 overflow-x-auto pb-2 items-start">
              {otherStates.map(({ step: s, label, caption }, idx) => (
                <div key={s} className="flex items-start gap-2 flex-shrink-0">
                  <div className="flex flex-col items-center gap-1.5" style={{ width: 160 }}>
                    {renderPreviewCard(s)}
                    <p className="text-[11px] font-semibold text-center leading-tight">{label}</p>
                    <p className="text-[10px] text-muted-foreground text-center leading-snug px-1">{caption}</p>
                  </div>
                  {idx < otherStates.length - 1 && (
                    <div
                      className="flex-shrink-0 text-2xl font-light select-none mt-32"
                      style={{ color: themeColor, opacity: 0.5 }}
                      aria-hidden
                    >
                      ·
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tackPreview.resetConfirmTitle", { language: LOCALE_LABELS[locale] || locale })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tackPreview.resetConfirmDesc", { language: LOCALE_LABELS[locale] || locale })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                changeLocale(locale);
                toast.success(t("tackPreview.resetToast", { language: LOCALE_LABELS[locale] || locale }));
                setShowResetConfirm(false);
              }}
            >
              {t("tackPreview.resetToDefaults")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </>
  );
}
